/* eslint-disable camelcase */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';

import { openModalPage } from 'actions/modalPage';
import {
  getCustomerGroupByID,
  getSelectedCustomerID,
  getSelectedCustomer,
} from 'reducers/customerSearch';
import { getWarehouseById } from 'reducers/warehouses';
import { getCurrencyFormatter } from 'reducers/configs/settings';
import { addError, addWarning, dismissType } from 'actions/Error';
import {
  useCustomerExtraData,
  useCustomerRewardPoints,
} from 'plugins/wbu/salesHistory/hooks';
import WBUAPI from 'plugins/wbu/API/API';
import { updateCustomer } from 'plugins/wbu/redux';
import { modalPages } from 'constants/modalPage';
import { RootState } from 'reducers';

function useDataTable() {
  const customer = useSelector(getSelectedCustomer);
  const { value: extraData } = useCustomerExtraData(customer.customerID);
  const formatCurrency = useSelector(getCurrencyFormatter);
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();

  const currentDate = dayjs(extraData?.dscExpiration ?? new Date()).format(
    'YYYY-MM-DD',
  );

  const [date, setDate] = useState<string>(currentDate);

  // When changing/updating customer, reset the date input field to the current date value
  useEffect(() => setDate(currentDate), [currentDate]);

  const expiration = extraData?.dscExpiration
    ? dayjs(extraData?.dscExpiration)
    : undefined;
  const expired = dayjs().isAfter(expiration ?? dayjs('1970-01-01T00:00:00'));

  const isSelectedCustomer =
    String(useSelector(getSelectedCustomerID)) === String(customer.customerID);
  const renewMembership = useCallback(async () => {
    if (expired) {
      dispatch(
        addWarning(
          'Cant renew customer membership, because he is not a member',
        ),
      );
      return;
    }
    try {
      await dispatch(WBUAPI.renewMembership(customer.id, date));
    } catch (error) {
      const message =
        (error as AxiosError).response?.data.message ||
        'Failed to renew customer membership';
      dispatch(
        addError(message, {
          selfDismiss: 5000,
          errorType: 'membership-renewal',
        }),
      );
      return;
    }
    dispatch(dismissType('membership-renewal'));
    if (isSelectedCustomer) {
      await dispatch(updateCustomer(customer.id));
    }
    // Wish we had react-query so i could just trigger a 'refetch' of the extra data
    dispatch(
      openModalPage({
        component: modalPages.customerView,
        replace: true,
        props: { customer },
      }),
    );
  }, [expired, dispatch, isSelectedCustomer, customer, date]);

  const customerRewardPoints = useCustomerRewardPoints(customer.customerID);
  const homeStore = useSelector(getWarehouseById(customer.homeStoreID));
  const group = useSelector(getCustomerGroupByID(customer.groupID));

  const lastVisit = extraData?.last_visit
    ? dayjs(extraData?.last_visit)
    : undefined;

  return {
    customer,
    customerRewardPoints,
    expired,
    extraData,
    formatCurrency,
    group,
    homeStore,
    lastVisit,
    renewMembership,
    date,
    setDate,
    currentDate,
  };
}

export default useDataTable;
